import { Capacitor } from "@capacitor/core";
import { isMobileWebBrowser } from "src/utils/platformUtils";
import { showErrorToast } from "src/components/AlertToastify/AlertToastify";
import { trackEndpointAccess } from "src/utils/analyticsCapFirebase";
import { getIsPollingRequest } from "src/utils/pollingContext";

interface ErrorResponse {
  detail?: string | { msg: string }[];
  message?: string;
}

export const getWaiverStream = async (
  authId: string,
  userDataPlatform: any,
  leagueIdPlatform: string,
  teamName: string,
  pocToken: string,
  accessAuthToken: string,
  signal?: AbortSignal,
  yahooToken?: string,
  audioSettings?: boolean,
  forceRefresh?: boolean,
  calculatorSettings?: any,
  model?: "RotoGPT 5.0" | "RotoGPT FREE",
  checkCacheOnly?: boolean
): Promise<Response | string | undefined> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/functions/waivers_streaming`;

    const requestData: any = {
      league_integration: {
        pocToken: pocToken,
        leagueToken: userDataPlatform.integration_name === "yahoo" ? yahooToken : userDataPlatform.token,
        user_id: userDataPlatform.user_id,
        league_id: leagueIdPlatform,
        integration_name: userDataPlatform.integration_name,
      },
      response_settings: {
        persona_input: "lombard_ai",
        // max_words: 0,
        response_type: "markdown",
        audio_settings: audioSettings,
      },
      sport: "NFL",
      model: model || "RotoGPT 5.0",
      force_refresh: forceRefresh,
      check_cache_only: checkCacheOnly,
      ...(calculatorSettings && { calculator_settings: calculatorSettings }),
    };

    // If the platform is ESPN, add the specific fields it requires
    if (userDataPlatform.integration_name === "espn") {
      requestData.league_integration.swid = userDataPlatform.swid;
      requestData.league_integration.espn_s2 = userDataPlatform.s2;
    }

    // console.log("requestData getWaiverStream", requestData);
    // console.log("getWaiverStream response_settings", requestData.response_settings);

    const options: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessAuthToken}`,
        Connection: "keep-alive", // Fuerza HTTP/2
        "Alt-Svc": "clear", // Fuerza HTTP/2
        ...(getIsPollingRequest() && { "X-Polling-Request": "true" }),
      },
      body: JSON.stringify(requestData),
      signal,
      credentials: "include",
    };

    const response = await fetch(url, options);

    // console.log(`[getWaiverStream] Received response from backend:`, {
    //   status: response.status,
    //   ok: response.ok,
    //   headers: Object.fromEntries(response.headers.entries()),
    // });

    if (!response.ok) {
      // Detect rate limit before reading body
      if (response.status === 429) {
        const err = new Error("TOO_MANY_REQUESTS") as any;
        err.isRateLimit = true;
        const data429 = await response.json().catch(() => ({}));
        err.retryAfter = data429?.retryAfter ?? 60;
        throw err;
      }

      const errorData = await response.json();
      let errorMessage;

      // if the error is an array, then get the first error message
      if (Array.isArray(errorData.detail)) {
        errorMessage = errorData.detail[0].msg;
      } else {
        // if the error is not an array, then get the error message
        errorMessage = errorData.detail || errorData.message;
      }

      console.error("[getWaiverStream] Error response from backend:", errorData);
      throw new Error(errorMessage);
      // add a flag to the error to indicate that it came from the initial request
      // const err = new Error(errorMessage) as Error & { fromInitialRequest?: boolean };
      // err.fromInitialRequest = true;
      // throw err;
    }

    // Check if response is JSON (INCOMPLETE) or Stream
    const contentType = response.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      // console.log("[getWaiverStream] Detected JSON response.");
      // Handle INCOMPLETE response for polling
      const jsonData = await response.json();
      // console.log("[getWaiverStream] Parsed JSON data:", jsonData);
      if (jsonData.status === "INCOMPLETE") {
        // console.log('[getWaiverStream] Status is INCOMPLETE, returning "INCOMPLETE".');
        return "INCOMPLETE"; // Special return value for polling detection
      }
      if (jsonData.status === "NOT_CACHED") {
        return "NOT_CACHED";
      }
    }

    // If we get here, it's the actual stream. Track the "real" execution now.
    if (response.ok) {
      await trackEndpointAccess(authId, userDataPlatform.integration_name, leagueIdPlatform, teamName, "waiver");
    }

    // Otherwise, handle as normal stream
    return response;
  } catch (error) {
    if (error.name === "AbortError") {
      // console.log("getWaiverStream fetch was aborted.");
      // return undefined; // Let the caller know it was intentionally aborted.
      throw error;
      
    }
    // Rate limit errors must never fall back to polling
    if ((error as any).isRateLimit) throw error;

    // Initial request returned 4xx/5xx: show Retry in UI, do not fall back to polling
    // if ((error as Error & { fromInitialRequest?: boolean }).fromInitialRequest) {
    //   throw error;
    // }
     if (Capacitor.isNativePlatform() || isMobileWebBrowser() || error.name === "TypeError") {
      // On native platforms, or for network errors on web (TypeError),
      // we gracefully fall back to polling, UNLESS it's a "no draft" error.
      if (error.message && error.message.includes("Rosters are empty")) {
        console.error("[getWaiverStream] Detected 'Rosters are empty' on native. Re-throwing error.", error);
        throw error; // Let HomePage handle this as a final error.
      }
      console.warn(`WaiverStream stream error (type: ${error.name}), falling back to polling:`, error);
      return "INCOMPLETE"; // Signal to the UI to start polling for completion.
    } else {
      // For any other type of error, re-throw it to be handled by the component.
      console.error("Error in getWaiverStream API call:", error);
      throw error;
    }
  }
};
